import { signOut } from 'firebase/auth';
import { FC, useCallback, useState } from 'react';
import { Container } from 'react-grid-system';
import toast from 'react-hot-toast';
import { NavLink } from 'react-router-dom';
import { Popover } from 'react-tiny-popover';

import { Button } from '..';
import { GotivaLogoNav, SettingsIcon } from '../../../assets/NavBar';
import { DashboardRoutePath } from '../../../modules/dashboard';
import { auth } from '../../../services';
import { strings } from '../../../strings';
import { DivFlex } from '../../Typhography';
import {
  DashNavLink,
  DashNavLinkPopover,
  GotivaLogo,
  Nav,
  NavIcons,
  NavList,
} from '../../Typhography/NavBarStyles';
import { ButtonPopover, PopoverCreateContainer } from '../../Typhography/PopoverNavStyle';
import Burger from '../Burger/Burger';

interface LinkPropsDirect {
  to: string;
  text: string;
  submenu?: never;
}

interface LinkPropsSubmenu {
  text: string;
  submenu: LinkPropsDirect[];
  to?: never;
}

type LinkProps = LinkPropsDirect | LinkPropsSubmenu;

const links: LinkProps[] = [
  { to: DashboardRoutePath.ARTISTS, text: strings.navArtistsTab },
  { to: DashboardRoutePath.PUZZLES, text: strings.navPuzzlesTab },
  { to: DashboardRoutePath.COLORING_GRIDS, text: 'Coloring Grids' },
  { to: DashboardRoutePath.DAILY_PUZZLES, text: strings.dailyPuzzlesTab },
  { to: DashboardRoutePath.GAME_EVENTS, text: 'Game Events' },
  { to: DashboardRoutePath.STORIES, text: 'Stories' },
  {
    text: strings.navCollectionsTab,
    submenu: [
      { to: DashboardRoutePath.COLLECTIONS, text: strings.navPuzzleCategoriesTab },
      {
        to: DashboardRoutePath.COLORING_GRID_COLLECTIONS,
        text: strings.navColoringGridCategoriesTab,
      },
    ],
  },
  // { to: DashboardRoutePath.ANALYTICS, text: strings.navAnalyticsTab },
  // { to: DashboardRoutePath.EVENTS, text: strings.navEventsTab },
  { to: DashboardRoutePath.THEMES, text: strings.navThemesTab },
  { to: DashboardRoutePath.PAYOUTS, text: strings.navPayoutsTab },
  // { to: DashboardRoutePath.INSIGHTS, text: strings.Insights },
  // { to: DashboardRoutePath.REVENUE, text: 'Revenue' },
  // { to: DashboardRoutePath.PAYOUT_REQUESTS, text: 'Payout Requests' },
];

const popoverCreateLinks = [
  { to: DashboardRoutePath.CREATE_PUZZLE, text: strings.navCreatePuzzleTab },
  { to: DashboardRoutePath.MANAGE_VIDEO_PUZZLE, text: strings.navCreateVideoPuzzleTab },
  { to: DashboardRoutePath.MANAGE_COLORING_GRIDS, text: strings.navManageColoringGridTab },
  { to: DashboardRoutePath.MANAGE_STORY, text: strings.navCreateStoryTab },
  { to: DashboardRoutePath.CREATE_COLLECTION, text: strings.navCreateCollectionTab },
  { to: DashboardRoutePath.CREATE_ARTISTS, text: strings.navCreateArtistTab },
  { to: DashboardRoutePath.CREATE_GAME_EVENTS, text: strings.navCreateGameEventTab },
  { to: DashboardRoutePath.MANAGE_THEME, text: strings.navCreateThemeTab },
];

const DashboardNavBar: FC = ({ children }) => {
  const [isPopoverCreateOpen, setIsPopoverCreateOpen] = useState<boolean>(false);
  const [isPopoverCategoriesOpen, setIsPopoverCategoriesOpen] = useState<boolean>(false);

  const doLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      toast.error(strings.toastNotificationSomethingWrong);
    }
  };

  const togglePopover = () => {
    setIsPopoverCreateOpen(!isPopoverCreateOpen);
  };

  const toggleCategoriesPopover = useCallback(() => {
    setIsPopoverCategoriesOpen(!isPopoverCategoriesOpen);
  }, []);

  return (
    <>
      <Nav>
        <DivFlex>
          <NavLink to={DashboardRoutePath.PUZZLES}>
            <GotivaLogo src={GotivaLogoNav} />
          </NavLink>
          <NavList>
            <Popover
              containerStyle={{ zIndex: '10' }}
              isOpen={isPopoverCreateOpen}
              positions={['top']}
              onClickOutside={() => setIsPopoverCreateOpen(false)}
              content={
                <PopoverCreateContainer>
                  {popoverCreateLinks.map((link: LinkProps, index: number) => (
                    <DashNavLinkPopover key={index} activeClassName="any" to={`/${link.to}`}>
                      {link.text}
                    </DashNavLinkPopover>
                  ))}
                </PopoverCreateContainer>
              }
            >
              <ButtonPopover onClick={togglePopover}>{strings.navCreateTab}</ButtonPopover>
            </Popover>
            {links.map((link: LinkProps, index: number) => {
              if (link.submenu) {
                return (
                  <Popover
                    key={index}
                    containerStyle={{ zIndex: '10' }}
                    isOpen={isPopoverCategoriesOpen}
                    positions={['top']}
                    onClickOutside={() => setIsPopoverCategoriesOpen(false)}
                    content={
                      <PopoverCreateContainer>
                        {link.submenu.map((link: LinkPropsDirect, index: number) => (
                          <DashNavLinkPopover key={index} activeClassName="any" to={`/${link.to}`}>
                            {link.text}
                          </DashNavLinkPopover>
                        ))}
                      </PopoverCreateContainer>
                    }
                  >
                    <ButtonPopover onClick={toggleCategoriesPopover}>{link.text}</ButtonPopover>
                  </Popover>
                );
              }
              return (
                <DashNavLink key={index} activeClassName="any" to={`/${link.to}`}>
                  {link.text}
                </DashNavLink>
              );
            })}
          </NavList>
        </DivFlex>
        <NavIcons alignItems="center">
          <NavLink
            style={{ color: 'gray' }}
            to={`/${DashboardRoutePath.SETTINGS}/${DashboardRoutePath.ACCOUNT_INFO}`}
          >
            <SettingsIcon />
          </NavLink>
          <Button onClick={doLogout} variant="link">
            {strings.logout}
          </Button>
        </NavIcons>
        <Burger />
      </Nav>
      <Container style={{ padding: '0' }} fluid>
        {children}
      </Container>
    </>
  );
};

export default DashboardNavBar;
